import { useState, useEffect } from 'react';

const About = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const images = [
    '/assets/images/about-img1.png',
    '/assets/images/about-img2.jpg',
    '/assets/images/about-img3.webp'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Left Side: About Us Info */}
          <div className="space-y-8">
            <h1 className="text-4xl md:text-5xl font-bold text-primary">About VitalHive</h1>
            <p className="text-xl text-primaryLight leading-relaxed">
              VitalHive is dedicated to revolutionizing healthcare by providing innovative solutions that connect patients and providers seamlessly. Our mission is to improve healthcare outcomes through technology.
            </p>
            
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="fas fa-bullseye text-secondary text-xl"></i>
                  <span className="text-xl font-semibold text-primary">Our Mission</span>
                </div>
                <p className="text-primaryLight leading-relaxed ml-8">
                  To empower healthcare providers with cutting-edge tools to deliver exceptional patient care.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="fas fa-eye text-secondary text-xl"></i>
                  <span className="text-xl font-semibold text-primary">Our Vision</span>
                </div>
                <p className="text-primaryLight leading-relaxed ml-8">
                  A world where technology enhances every aspect of the healthcare journey, making it more accessible for everyone.
                </p>
              </div>
            </div>
          </div>

          {/* Right Side: Image Slideshow */}
          <div className="relative">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-card">
              <img 
                src={images[currentImageIndex]} 
                alt="About VitalHive" 
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
            </div>
            
            {/* Slideshow dots */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-3">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToImage(index)}
                  className={`w-3 h-3 rounded-full transition-colors duration-300 ${
                    index === currentImageIndex ? 'bg-secondary' : 'bg-white/50'
                  }`}
                ></button>
              ))}
            </div>
          </div>
        </div>

        {/* Our Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-primary">Our Story</h2>
            <p className="text-primaryLight leading-relaxed">
              VitalHive was born out of a simple but powerful idea — that healthcare should be more connected, more responsive, and more human. We're a team of technologists and dreamers committed to solving real problems that providers and patients face every day.
            </p>
          </div>
          
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-primary">Our Values</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="fas fa-heart text-secondary mt-1"></i>
                <div>
                  <h3 className="font-semibold text-primary">Compassion</h3>
                  <p className="text-primaryLight text-sm">We care deeply about the people we serve</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="fas fa-lightbulb text-secondary mt-1"></i>
                <div>
                  <h3 className="font-semibold text-primary">Innovation</h3>
                  <p className="text-primaryLight text-sm">We constantly push boundaries to improve healthcare</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <i className="fas fa-shield-alt text-secondary mt-1"></i>
                <div>
                  <h3 className="font-semibold text-primary">Trust</h3>
                  <p className="text-primaryLight text-sm">We build reliable, secure solutions you can depend on</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About; 